import React, { useEffect, useMemo, useState } from 'react';
import {
    CCard,
    CCardBody,
    CCol,
    CRow,
    CButton,
    CFormInput,
    CFormCheck,
    CFormSwitch,
    CFormSelect,
    CTable,
    CTableHead,
    CTableRow,
    CTableHeaderCell,
    CTableBody,
    CTableDataCell,
    CAlert,
    CTooltip,
} from '@coreui/react-pro';
import CIcon from '@coreui/icons-react';
import { cilArrowBottom, cilArrowTop, cilPlus, cilTrash } from '@coreui/icons';
import * as DomicilappService from '../../services/DomicilappService';
import { ProductToppingGroup, StoreTopping, StoreToppingGroup, ToppingGroupItem, ToppingRules } from '../../types/product-form';

const genId = (prefix: string) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const formatCurrency = (val: number) =>
    new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(val || 0);

/** Variante del producto a la que se le pueden dar reglas propias (ej. "Universitario") */
export interface ToppingVariantOption {
    id: string;
    label: string;
}

const validateRules = (rules: ToppingRules, availableUnits: number, where: string): string | null => {
    const { min_selections: min, max_selections: max, free_selections: free } = rules;
    if (max !== null && max < 1) return `${where} el máximo debe ser al menos 1.`;
    if (max !== null && min > max) return `${where} el mínimo no puede ser mayor al máximo.`;
    if (max !== null && free !== null && free > max) return `${where} los gratis no pueden superar el máximo.`;
    if (rules.max_per_topping < 1) return `${where} cada topping debe poderse elegir al menos 1 vez.`;
    if (min > availableUnits * rules.max_per_topping) return `${where} el mínimo exige más toppings de los que hay disponibles.`;
    return null;
};

// Mismas reglas que valida el backend al guardar y el carrito al comprar
export const validateToppingGroups = (groups: ProductToppingGroup[], variants: ToppingVariantOption[] = []): string | null => {
    for (const group of groups) {
        const name = group.name.trim() || 'Sin nombre';
        if (!group.name.trim()) return 'Cada grupo de toppings necesita un nombre.';
        if (group.items.length === 0) return `El grupo "${name}" no tiene toppings.`;
        if (group.items.some((i) => !i.name.trim())) return `Hay un topping sin nombre en "${name}".`;
        const available = group.items.filter((i) => i.is_available).length;
        const error = validateRules(group, available, `En "${name}"`);
        if (error) return error;
        for (const rule of group.variant_rules) {
            const variant = variants.find((v) => v.id === rule.variant_id);
            if (!variant) continue;
            const variantError = validateRules(rule, available, `En "${name}" (${variant.label})`);
            if (variantError) return variantError;
        }
    }
    return null;
};

// Resumen legible de las reglas, como lo verá el cliente
const describeRules = (r: ToppingRules) => {
    const parts = [r.min_selections > 0 ? `Obligatorio: elige mínimo ${r.min_selections}` : 'Opcional'];
    parts.push(r.max_selections === null ? 'sin límite' : `máximo ${r.max_selections}`);
    if (r.free_selections === null) parts.push('todos gratis');
    else if (r.free_selections > 0) parts.push(`${r.free_selections} gratis`);
    if (r.max_per_topping > 1) parts.push(`cada uno hasta ${r.max_per_topping} veces`);
    return parts.join(' · ');
};

const numberInput = (value: number, onValue: (n: number) => void, min = 0, disabled = false) => (
    <CFormInput
        size="sm"
        type="number"
        min={min}
        value={disabled ? '' : value}
        disabled={disabled}
        onChange={(e) => onValue(Math.max(min, Math.floor(Number(e.target.value) || 0)))}
    />
);

// Mínimo, máximo (o sin límite), gratis (o todos) y repeticiones: products_toppings_groups
const RulesInputs: React.FC<{ id: string; rules: ToppingRules; onChange: (patch: Partial<ToppingRules>) => void }> = ({ id, rules, onChange }) => (
    <CRow className="g-2">
        <CCol xs={6} md={3}>
            <label className="form-label small fw-semibold text-body mb-1">Mínimo a elegir</label>
            {numberInput(rules.min_selections, (n) => onChange({ min_selections: n }))}
        </CCol>
        <CCol xs={6} md={3}>
            <label className="form-label small fw-semibold text-body mb-1">Máximo a elegir</label>
            {numberInput(rules.max_selections ?? 0, (n) => onChange({ max_selections: n }), 1, rules.max_selections === null)}
            <CFormCheck
                id={`${id}-unlimited`}
                className="small mt-1"
                label="Sin límite"
                checked={rules.max_selections === null}
                onChange={(e) => onChange({ max_selections: e.target.checked ? null : Math.max(1, rules.min_selections) })}
            />
        </CCol>
        <CCol xs={6} md={3}>
            <label className="form-label small fw-semibold text-body mb-1">Gratis</label>
            {numberInput(rules.free_selections ?? 0, (n) => onChange({ free_selections: n }), 0, rules.free_selections === null)}
            <CFormCheck
                id={`${id}-all-free`}
                className="small mt-1"
                label="Todos gratis"
                checked={rules.free_selections === null}
                onChange={(e) => onChange({ free_selections: e.target.checked ? null : 0 })}
            />
        </CCol>
        <CCol xs={6} md={3}>
            <label className="form-label small fw-semibold text-body mb-1">Repetir un topping hasta</label>
            {numberInput(rules.max_per_topping, (n) => onChange({ max_per_topping: n }), 1)}
        </CCol>
    </CRow>
);

interface ToppingsStepProps {
    storeId: string;
    groups: ProductToppingGroup[];
    onChange: (groups: ProductToppingGroup[]) => void;
    // Variantes del producto (vacío si es un producto simple)
    variants: ToppingVariantOption[];
}

export const ToppingsStep: React.FC<ToppingsStepProps> = ({ storeId, groups, onChange, variants }) => {
    const [toppingCatalog, setToppingCatalog] = useState<StoreTopping[]>([]);
    const [groupCatalog, setGroupCatalog] = useState<StoreToppingGroup[]>([]);
    const [groupToReuse, setGroupToReuse] = useState('');
    const [newItem, setNewItem] = useState<Record<string, { name: string; price: number | '' }>>({});

    useEffect(() => {
        if (!storeId) return;
        DomicilappService.getToppingsByStore(storeId)
            .then((response) => setToppingCatalog(response?.data ?? []))
            .catch(() => setToppingCatalog([]));
        DomicilappService.getToppingGroupsByStore(storeId)
            .then((response) => setGroupCatalog(response?.data ?? []))
            .catch(() => setGroupCatalog([]));
    }, [storeId]);

    const reusableGroups = useMemo(
        () => groupCatalog.filter((g) => !groups.some((pg) => pg.id === String(g.id))),
        [groupCatalog, groups],
    );

    const updateGroup = (groupId: string, patch: Partial<ProductToppingGroup>) =>
        onChange(groups.map((g) => (g.id === groupId ? { ...g, ...patch } : g)));

    const updateItem = (groupId: string, clientId: string, patch: Partial<ToppingGroupItem>) => {
        const group = groups.find((g) => g.id === groupId);
        if (!group) return;
        updateGroup(groupId, { items: group.items.map((i) => (i.client_id === clientId ? { ...i, ...patch } : i)) });
    };

    const addGroup = () =>
        onChange([
            ...groups,
            { id: genId('tg'), name: '', min_selections: 0, max_selections: 1, free_selections: 0, max_per_topping: 1, items: [], variant_rules: [] },
        ]);

    // Reglas propias de una variante: se activan copiando las del grupo
    const toggleVariantRule = (group: ProductToppingGroup, variantId: string, enabled: boolean) => {
        const { min_selections, max_selections, free_selections, max_per_topping } = group;
        updateGroup(group.id, {
            variant_rules: enabled
                ? [...group.variant_rules, { variant_id: variantId, min_selections, max_selections, free_selections, max_per_topping }]
                : group.variant_rules.filter((r) => r.variant_id !== variantId),
        });
    };

    const updateVariantRule = (group: ProductToppingGroup, variantId: string, patch: Partial<ToppingRules>) =>
        updateGroup(group.id, { variant_rules: group.variant_rules.map((r) => (r.variant_id === variantId ? { ...r, ...patch } : r)) });

    const reuseGroup = () => {
        const source = groupCatalog.find((g) => String(g.id) === groupToReuse);
        if (!source) return;
        onChange([
            ...groups,
            {
                id: String(source.id),
                name: source.name,
                min_selections: 0,
                max_selections: Math.max(1, Math.min(source.items.length, 3)),
                free_selections: 0,
                max_per_topping: 1,
                // Al guardar, este producto se suma a los que ya lo usan
                products_count: source.products_count + 1,
                items: source.items.map((i) => ({ ...i, client_id: genId('ti') })),
                variant_rules: [],
            },
        ]);
        setGroupToReuse('');
    };

    const removeGroup = (groupId: string) => onChange(groups.filter((g) => g.id !== groupId));

    const moveGroup = (index: number, direction: -1 | 1) => {
        const target = index + direction;
        if (target < 0 || target >= groups.length) return;
        const next = [...groups];
        [next[index], next[target]] = [next[target], next[index]];
        onChange(next);
    };

    // Si el nombre coincide con un topping del catálogo se reutiliza; si no, el backend lo crea
    const matchCatalog = (name: string) =>
        toppingCatalog.find((t) => t.name.trim().toLowerCase() === name.trim().toLowerCase());

    const addItem = (groupId: string) => {
        const draft = newItem[groupId];
        const name = draft?.name.trim() ?? '';
        const group = groups.find((g) => g.id === groupId);
        if (!name || !group) return;
        if (group.items.some((i) => i.name.trim().toLowerCase() === name.toLowerCase())) return;
        const existing = matchCatalog(name);
        updateGroup(groupId, {
            items: [
                ...group.items,
                {
                    client_id: genId('ti'),
                    topping_id: existing?.id ?? null,
                    name: existing?.name ?? name,
                    price: draft?.price === '' || draft?.price === undefined ? 0 : Number(draft.price),
                    is_default: false,
                    is_available: true,
                },
            ],
        });
        setNewItem((prev) => ({ ...prev, [groupId]: { name: '', price: '' } }));
    };

    return (
        <div>
            <h5 className="fw-bold text-dark mb-1">Toppings del Producto</h5>
            <p className="text-muted small mb-4">
                Agrupa las opciones que el cliente elige al armar su pedido (ej. "Salsas", "Proteína", "Toppings de helado"). Cada grupo
                define cuántas puede elegir, cuántas son gratis y el precio de cada topping.
            </p>

            <datalist id="store-toppings-catalog">
                {toppingCatalog.map((t) => (
                    <option key={t.id} value={t.name} />
                ))}
            </datalist>

            {groups.length === 0 && (
                <div className="p-4 text-center bg-light rounded-3 border mb-3">
                    <p className="mb-0 text-muted small">Este producto no tiene toppings. Es opcional: puedes continuar sin agregar grupos.</p>
                </div>
            )}

            {groups.map((group, index) => {
                const draft = newItem[group.id] ?? { name: '', price: '' };
                // products_count incluye este producto
                const isShared = (group.products_count ?? 0) > 1;
                return (
                    <CCard key={group.id} className="border mb-3 rounded-3 shadow-none bg-white">
                        <CCardBody className="p-3">
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <CFormInput
                                    placeholder="Nombre del grupo (ej. Salsas)"
                                    value={group.name}
                                    onChange={(e) => updateGroup(group.id, { name: e.target.value })}
                                    className="fw-semibold"
                                    style={{ maxWidth: '320px' }}
                                />
                                <span className="text-muted small d-none d-md-inline">{describeRules(group)}</span>
                                <div className="ms-auto d-flex gap-1">
                                    <CButton size="sm" color="light" className="border" disabled={index === 0} onClick={() => moveGroup(index, -1)}>
                                        <CIcon icon={cilArrowTop} size="sm" />
                                    </CButton>
                                    <CButton size="sm" color="light" className="border" disabled={index === groups.length - 1} onClick={() => moveGroup(index, 1)}>
                                        <CIcon icon={cilArrowBottom} size="sm" />
                                    </CButton>
                                    <CTooltip content="Quitar grupo de este producto">
                                        <CButton size="sm" color="light" className="border text-danger" onClick={() => removeGroup(group.id)}>
                                            <CIcon icon={cilTrash} size="sm" />
                                        </CButton>
                                    </CTooltip>
                                </div>
                            </div>

                            {isShared && (
                                <CAlert color="warning" className="py-2 small">
                                    Este grupo también lo usan otros productos ({group.products_count} en total, contando este). Los cambios en su nombre, toppings y
                                    precios aplican a todos; las reglas de selección son solo de este producto.
                                </CAlert>
                            )}

                            {/* REGLAS (products_toppings_groups) */}
                            <RulesInputs id={group.id} rules={group} onChange={(patch) => updateGroup(group.id, patch)} />
                            <p className="text-muted small mt-2 mb-3">
                                "Gratis" no cobra las primeras unidades más baratas que elija el cliente. Mínimo 0 = el grupo es opcional.
                            </p>

                            {/* REGLAS POR VARIANTE (products_toppings_groups.products_variants_id) */}
                            {variants.length > 0 && (
                                <div className="border rounded-3 p-2 mb-3">
                                    <p className="small fw-semibold text-body mb-1">Reglas por variante</p>
                                    <p className="text-muted small mb-2">
                                        Las variantes usan las reglas de arriba. Activa reglas propias cuando una variante permita elegir otra cantidad
                                        (ej. Universitario: máximo 3 · Premium: sin límite).
                                    </p>
                                    {variants.map((variant) => {
                                        const rule = group.variant_rules.find((r) => r.variant_id === variant.id);
                                        return (
                                            <div key={variant.id} className="py-2 border-top">
                                                <div className="d-flex align-items-center gap-2">
                                                    <CFormSwitch
                                                        id={`${group.id}-${variant.id}-own`}
                                                        label={<span className="small fw-semibold">{variant.label}</span>}
                                                        checked={!!rule}
                                                        onChange={(e) => toggleVariantRule(group, variant.id, e.target.checked)}
                                                    />
                                                    <span className="text-muted small ms-auto d-none d-md-inline">
                                                        {rule ? describeRules(rule) : 'Usa las reglas del grupo'}
                                                    </span>
                                                </div>
                                                {rule && (
                                                    <div className="mt-2">
                                                        <RulesInputs
                                                            id={`${group.id}-${variant.id}`}
                                                            rules={rule}
                                                            onChange={(patch) => updateVariantRule(group, variant.id, patch)}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* TOPPINGS DEL GRUPO (toppings_groups_items) */}
                            {group.items.length > 0 && (
                                <CTable small align="middle" bordered className="bg-white mb-2">
                                    <CTableHead color="light">
                                        <CTableRow>
                                            <CTableHeaderCell className="text-secondary small fw-bold">Topping</CTableHeaderCell>
                                            <CTableHeaderCell className="text-secondary small fw-bold" style={{ width: '150px' }}>Precio ($)</CTableHeaderCell>
                                            <CTableHeaderCell className="text-secondary small fw-bold text-center" style={{ width: '110px' }}>Premarcado</CTableHeaderCell>
                                            <CTableHeaderCell className="text-secondary small fw-bold text-center" style={{ width: '110px' }}>Disponible</CTableHeaderCell>
                                            <CTableHeaderCell style={{ width: '50px' }} />
                                        </CTableRow>
                                    </CTableHead>
                                    <CTableBody>
                                        {group.items.map((item) => (
                                            <CTableRow key={item.client_id}>
                                                <CTableDataCell>
                                                    <CFormInput
                                                        size="sm"
                                                        list="store-toppings-catalog"
                                                        value={item.name}
                                                        onChange={(e) => {
                                                            const match = matchCatalog(e.target.value);
                                                            updateItem(group.id, item.client_id, { name: e.target.value, topping_id: match?.id ?? null });
                                                        }}
                                                    />
                                                </CTableDataCell>
                                                <CTableDataCell>
                                                    <CFormInput
                                                        size="sm"
                                                        type="number"
                                                        min={0}
                                                        value={item.price}
                                                        onChange={(e) => updateItem(group.id, item.client_id, { price: Math.max(0, Number(e.target.value) || 0) })}
                                                    />
                                                </CTableDataCell>
                                                <CTableDataCell className="text-center">
                                                    <CFormCheck
                                                        checked={item.is_default}
                                                        onChange={(e) => updateItem(group.id, item.client_id, { is_default: e.target.checked })}
                                                    />
                                                </CTableDataCell>
                                                <CTableDataCell className="text-center">
                                                    <CFormSwitch
                                                        className="d-inline-block"
                                                        checked={item.is_available}
                                                        onChange={(e) => updateItem(group.id, item.client_id, { is_available: e.target.checked })}
                                                    />
                                                </CTableDataCell>
                                                <CTableDataCell className="text-center">
                                                    <CButton
                                                        color="link"
                                                        className="text-secondary p-0"
                                                        onClick={() => updateGroup(group.id, { items: group.items.filter((i) => i.client_id !== item.client_id) })}
                                                    >
                                                        <CIcon icon={cilTrash} />
                                                    </CButton>
                                                </CTableDataCell>
                                            </CTableRow>
                                        ))}
                                    </CTableBody>
                                </CTable>
                            )}

                            <div className="d-flex flex-wrap gap-2" style={{ maxWidth: '520px' }}>
                                <CFormInput
                                    size="sm"
                                    list="store-toppings-catalog"
                                    placeholder="Agregar topping (ej. Salsa de ajo)"
                                    value={draft.name}
                                    onChange={(e) => setNewItem((prev) => ({ ...prev, [group.id]: { ...draft, name: e.target.value } }))}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            addItem(group.id);
                                        }
                                    }}
                                    style={{ flex: '1 1 200px' }}
                                />
                                <CFormInput
                                    size="sm"
                                    type="number"
                                    min={0}
                                    placeholder="Precio"
                                    value={draft.price}
                                    onChange={(e) =>
                                        setNewItem((prev) => ({ ...prev, [group.id]: { ...draft, price: e.target.value === '' ? '' : Number(e.target.value) } }))
                                    }
                                    style={{ width: '110px' }}
                                />
                                <CButton size="sm" color="dark" className="text-white fw-semibold" onClick={() => addItem(group.id)}>
                                    <CIcon icon={cilPlus} className="me-1" /> Agregar
                                </CButton>
                            </div>
                            {group.items.length > 0 && (
                                <p className="text-muted small mt-2 mb-0">
                                    Precios: {group.items.map((i) => `${i.name || '—'} ${i.price > 0 ? formatCurrency(i.price) : 'sin costo'}`).join(' · ')}
                                </p>
                            )}
                        </CCardBody>
                    </CCard>
                );
            })}

            <div className="d-flex flex-wrap gap-2 mt-3">
                <CButton color="dark" className="text-white fw-semibold" onClick={addGroup}>
                    <CIcon icon={cilPlus} className="me-1" /> Nuevo grupo
                </CButton>
                {reusableGroups.length > 0 && (
                    <div className="d-flex gap-2" style={{ maxWidth: '420px' }}>
                        <CFormSelect value={groupToReuse} onChange={(e) => setGroupToReuse(e.target.value)}>
                            <option value="">Reutilizar un grupo de la tienda...</option>
                            {reusableGroups.map((g) => (
                                <option key={g.id} value={g.id}>
                                    {g.name} ({g.items.length} toppings)
                                </option>
                            ))}
                        </CFormSelect>
                        <CButton color="light" className="border fw-semibold text-nowrap" disabled={!groupToReuse} onClick={reuseGroup}>
                            Usar
                        </CButton>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ToppingsStep;
